/**
 * Session d'appels (conception 7.4) : etat d'une session, passage au suivant,
 * bilan, regles du prechargement des scripts, raccourcis clavier et
 * conservation dans l'onglet (sessionStorage). Aucune dependance a React : la
 * page ne fait qu'orchestrer.
 */
import { z } from 'zod';
import { MOTIFS_FILE_APPELS, RESULTATS_INTERACTION } from '@leads/shared';
import type { ElementFileAppelsDTO, ResultatInteraction } from '@leads/shared';
import { ErreurApi } from './api';
import { extraitDeNotes } from './format';

/** Les six resultats de la maquette validee, dans l'ordre des touches 1 a 6. */
export const RESULTATS_SESSION = [
  'NON_JOIGNABLE', 'REPONDEUR', 'RAPPEL_DEMANDE', 'INTERESSE', 'RDV_FIXE', 'PAS_INTERESSE',
] as const satisfies readonly ResultatInteraction[];
export type ResultatSession = (typeof RESULTATS_SESSION)[number];

/** Quelqu'un a decroche : les « joints » du bilan. Pas de reponse et repondeur n'en sont pas. */
const RESULTATS_JOINTS: ReadonlySet<ResultatInteraction> = new Set<ResultatInteraction>([
  'JOIGNABLE', 'RAPPEL_DEMANDE', 'INTERESSE', 'RDV_FIXE', 'PAS_INTERESSE',
]);

const schemaIssue = z.discriminatedUnion('type', [
  // Appel consigne : une interaction enregistree.
  z.object({ type: z.literal('appel'), resultat: z.enum(RESULTATS_INTERACTION), relancePlanifiee: z.boolean() }),
  // « Passer » : rien d'enregistre.
  z.object({ type: z.literal('passe') }),
  // Pris par un collegue entre-temps ou en opposition (404, 409 LEAD_OPPOSANT ou LEAD_DEJA_ATTRIBUE) : retire de la session.
  z.object({ type: z.literal('indisponible') }),
]);
export type IssueAppel = z.infer<typeof schemaIssue>;

/**
 * Ce que l'ecran lit de chaque element restaure : un element d'une autre forme planterait la page a chaque
 * rechargement, jusqu'a minuit. Le reste de l'element n'est qu'affiche.
 */
const schemaElementFile = z.object({
  lead: z.object({ id: z.string(), denomination: z.string() }),
  telephone: z.string(),
  motif: z.enum(MOTIFS_FILE_APPELS),
});

function estElementFile(valeur: unknown): valeur is ElementFileAppelsDTO {
  return schemaElementFile.safeParse(valeur).success;
}

const schemaEtat = z.object({
  // Toute modification de la forme de l'etat ou d'un element de la file impose d'incrementer la version : une
  // session gardee par la version precedente, le jour meme d'un deploiement, est alors effacee plutot que mal lue.
  version: z.literal(1),
  utilisateurId: z.string(),
  /** Jour de Paris du demarrage : une session ne survit pas a minuit. */
  jour: z.string(),
  /**
   * File figee au demarrage. Relue apres un rechargement, celle du serveur a
   * change (les leads appeles en sont sortis, d'autres y sont entres) : la
   * session reste la liste que l'on a demarree.
   */
  elements: z.array(z.custom<ElementFileAppelsDTO>(estElementFile)),
  /** Lead affiche ; au-dela du dernier, plus rien a appeler. */
  index: z.number().int().min(0),
  /** Issue de chaque lead traite, par identifiant. */
  issues: z.record(z.string(), schemaIssue),
  /** Scripts deja demandes par la session : jamais deux fois. */
  scriptsDemandes: z.array(z.string()),
  /** 429 recu : plus de prechargement pour cette session. */
  quotaAtteint: z.boolean(),
  terminee: z.boolean(),
});
export type EtatSessionAppels = z.infer<typeof schemaEtat>;

/**
 * Element aux notes abregees telles que la fiche les montre (`extraitDeNotes`), sans jamais modifier celui recu :
 * la file vient du cache de requetes.
 */
function alleger(element: ElementFileAppelsDTO): ElementFileAppelsDTO {
  const echange = element.dernierEchange;
  if (!echange || echange.notes === null) return element;
  return { ...element, dernierEchange: { ...echange, notes: extraitDeNotes(echange.notes) } };
}

/**
 * Session neuve sur la file du moment. La file entiere est reecrite dans
 * l'onglet a chaque changement d'etat : des notes de 20 000 caracteres pour
 * 220 leads depasseraient son quota, et la reprise serait perdue en silence.
 */
export function demarrerSession(utilisateurId: string, jour: string, elements: readonly ElementFileAppelsDTO[]): EtatSessionAppels {
  return { version: 1, utilisateurId, jour, elements: elements.map(alleger), index: 0, issues: {}, scriptsDemandes: [], quotaAtteint: false, terminee: false };
}

/** Premier lead non traite a partir de `depuis`. */
function prochainIndex(etat: Pick<EtatSessionAppels, 'elements' | 'issues'>, depuis: number): number {
  let index = depuis;
  while (index < etat.elements.length && etat.issues[etat.elements[index]?.lead.id ?? ''] !== undefined) index += 1;
  return index;
}

export function elementCourant(etat: EtatSessionAppels): ElementFileAppelsDTO | null {
  return etat.terminee ? null : (etat.elements[etat.index] ?? null);
}

/**
 * Consigne l'issue d'un lead. Le lead affiche traite, la session passe au
 * suivant non traite ; un lead plus loin (retire pendant le prechargement)
 * sera saute quand on y arrivera.
 */
export function consigner(etat: EtatSessionAppels, leadId: string, issue: IssueAppel): EtatSessionAppels {
  const issues = { ...etat.issues, [leadId]: issue };
  const courant = etat.elements[etat.index]?.lead.id === leadId;
  return { ...etat, issues, index: courant ? prochainIndex({ elements: etat.elements, issues }, etat.index + 1) : etat.index };
}

export function noterScriptDemande(etat: EtatSessionAppels, leadId: string): EtatSessionAppels {
  return etat.scriptsDemandes.includes(leadId) ? etat : { ...etat, scriptsDemandes: [...etat.scriptsDemandes, leadId] };
}

export function noterQuotaAtteint(etat: EtatSessionAppels): EtatSessionAppels {
  return etat.quotaAtteint ? etat : { ...etat, quotaAtteint: true };
}

export function terminerSession(etat: EtatSessionAppels): EtatSessionAppels {
  return { ...etat, terminee: true };
}

/** « Appel n sur N » et barre de progression. */
export function progression(etat: EtatSessionAppels): { numero: number; total: number; traites: number; restants: number } {
  const total = etat.elements.length;
  const traites = etat.elements.filter((element) => etat.issues[element.lead.id] !== undefined).length;
  return { numero: Math.min(etat.index + 1, total), total, traites, restants: total - traites };
}

/**
 * Script a demander (conception 7.4) : ni script pret de moins de
 * JOURS_VALIDITE_SCRIPT jours (signale par `pretId`, calcule par l'API a la
 * lecture de la file), ni generation en file ou en cours.
 */
export function scriptAPrecharger(element: ElementFileAppelsDTO): boolean {
  const script = element.script;
  if (!script) return true;
  return script.pretId === null && script.dernierStatut !== 'EN_ATTENTE' && script.dernierStatut !== 'EN_COURS';
}

/** Sur le lead n : les leads n+1 et n+2 non traites dont le script reste a demander. */
export function aPrecharger(etat: EtatSessionAppels): ElementFileAppelsDTO[] {
  if (etat.quotaAtteint || elementCourant(etat) === null) return [];
  return etat.elements
    .slice(etat.index + 1)
    .filter((element) => etat.issues[element.lead.id] === undefined)
    .slice(0, 2)
    .filter((element) => scriptAPrecharger(element) && !etat.scriptsDemandes.includes(element.lead.id));
}

export type Refus = 'indisponible' | 'quota' | 'autre';

/**
 * Refus de l'API sur un lead de la file, lu par code. Lead retire de la
 * session : 404 (devenu invisible, pris par un collegue), 409 LEAD_OPPOSANT ou
 * LEAD_DEJA_ATTRIBUE. Quota de scripts du jour atteint : 429 QUOTA_DEPASSE.
 * Tout le reste (429 du limiteur de debit, TROP_DE_REQUETES, qui se libere en
 * une minute ; 409 CONFLIT_CONCURRENT rejouable ; 403 ; panne ; reseau) n'est
 * ni un lead perdu ni le quota : l'appel n'est simplement pas enregistre.
 */
export function lireRefus(erreur: unknown): Refus {
  if (!(erreur instanceof ErreurApi)) return 'autre';
  // Un debit pris pour le quota couperait le prechargement jusqu'a la fin de la session, alors que le limiteur se libere en une minute.
  if (erreur.statut === 429 && erreur.code === 'QUOTA_DEPASSE') return 'quota';
  if (erreur.statut === 404) return 'indisponible';
  // Opposition ou prise par un collegue ; un conflit rejouable (CONFLIT_CONCURRENT) n'est pas un lead perdu.
  if (erreur.statut === 409 && (erreur.code === 'LEAD_OPPOSANT' || erreur.code === 'LEAD_DEJA_ATTRIBUE')) return 'indisponible';
  return 'autre';
}

/** Chiffres du bilan de fin de session (le composant `BilanSession` les affiche). */
export interface ChiffresBilan {
  appels: number;
  joints: number;
  rendezVous: number;
  relancesPlanifiees: number;
  passes: number;
  indisponibles: number;
  restants: number;
  /** Leads ou un rendez-vous a ete fixe, pour y revenir. */
  leadsRendezVous: Array<{ id: string; denomination: string }>;
}

export function bilanSession(etat: EtatSessionAppels): ChiffresBilan {
  const bilan: ChiffresBilan = {
    appels: 0, joints: 0, rendezVous: 0, relancesPlanifiees: 0, passes: 0, indisponibles: 0, restants: 0, leadsRendezVous: [],
  };
  for (const element of etat.elements) {
    const issue = etat.issues[element.lead.id];
    if (issue === undefined) bilan.restants += 1;
    else if (issue.type === 'passe') bilan.passes += 1;
    else if (issue.type === 'indisponible') bilan.indisponibles += 1;
    else {
      bilan.appels += 1;
      if (RESULTATS_JOINTS.has(issue.resultat)) bilan.joints += 1;
      if (issue.relancePlanifiee) bilan.relancesPlanifiees += 1;
      if (issue.resultat === 'RDV_FIXE') {
        bilan.rendezVous += 1;
        bilan.leadsRendezVous.push({ id: element.lead.id, denomination: element.lead.denomination });
      }
    }
  }
  return bilan;
}

/* -------------------------------- Clavier --------------------------------- */

/**
 * `input` ou un chiffre n'est pas du texte : cases, boutons, curseur, couleur,
 * fichier. Tout autre type se tape, dates et heures comprises.
 */
const TYPES_INPUT_SANS_TEXTE: ReadonlySet<string> = new Set([
  'checkbox', 'radio', 'button', 'submit', 'reset', 'image', 'range', 'color', 'file',
]);

/**
 * Un chiffre tape dans un champ est du texte, pas un raccourci. Une case, un
 * bouton radio ou un bouton gardent le focus apres un clic ou l'Espace : les
 * raccourcis y restent actifs.
 */
export function estChampDeSaisie(cible: EventTarget | null): boolean {
  if (cible instanceof HTMLInputElement) return !TYPES_INPUT_SANS_TEXTE.has(cible.type);
  return (
    cible instanceof HTMLTextAreaElement ||
    cible instanceof HTMLSelectElement ||
    // `=== true` : jsdom n'implemente pas `isContentEditable` (undefined), le resultat reste un booleen.
    (cible instanceof HTMLElement && cible.isContentEditable === true)
  );
}

/**
 * Resultat d'une touche 1 a 6, `null` sinon. La rangee des chiffres se lit sur
 * la touche physique (`code`) : sur un clavier AZERTY, elle donne « & »,
 * « e accent aigu »... sans Maj. Le reste, pave numerique compris, se lit sur
 * `key` : verrouille, le pave garde le `code` `Numpad2` mais envoie une fleche.
 */
export function resultatDeTouche(
  evenement: Pick<KeyboardEvent, 'key' | 'code' | 'altKey' | 'ctrlKey' | 'metaKey' | 'target'>,
): ResultatSession | null {
  if (evenement.altKey || evenement.ctrlKey || evenement.metaKey || estChampDeSaisie(evenement.target)) return null;
  const chiffre = /^Digit([1-6])$/.exec(evenement.code)?.[1] ?? (/^[1-6]$/.test(evenement.key) ? evenement.key : undefined);
  return chiffre === undefined ? null : (RESULTATS_SESSION[Number(chiffre) - 1] ?? null);
}

/* ----------------------------- Conservation -------------------------------- */

export const CLE_SESSION_APPELS = 'leads.session-appels';

/**
 * Session en cours de ce compte et de ce jour ; tout autre contenu (autre
 * compte, autre jour, forme invalide, texte illisible) est efface : les noms,
 * numeros et notes d'une session ne restent pas dans l'onglet.
 */
export function lireSessionAppels(utilisateurId: string, jour: string): EtatSessionAppels | null {
  try {
    const brut = window.sessionStorage.getItem(CLE_SESSION_APPELS);
    if (brut === null) return null;
    const lu = schemaEtat.safeParse(JSON.parse(brut));
    if (lu.success && lu.data.utilisateurId === utilisateurId && lu.data.jour === jour) return lu.data;
  } catch {
    // Stockage bloque, ou contenu qui n'est pas du JSON : traite comme tout autre contenu non repris.
  }
  effacerSessionAppels();
  return null;
}

/**
 * Session a reprendre a l'ouverture de l'ecran : celle de ce compte et de ce jour, tant qu'il lui reste un lead
 * a appeler. Terminee, elle ne l'est pas -- son bilan ne se voit qu'a la fin -- et elle est effacee : l'ecran de
 * depart propose la file du moment.
 */
export function reprendreSessionAppels(utilisateurId: string, jour: string): EtatSessionAppels | null {
  const etat = lireSessionAppels(utilisateurId, jour);
  if (etat === null || elementCourant(etat) !== null) return etat;
  effacerSessionAppels();
  return null;
}

/**
 * Epoque de purge : augmente a chaque fin de session du compte (deconnexion, 401, autre compte : `purgerSession`).
 * Un changement d'etat engage juste avant la purge garde son ecriture en attente (effet de l'ecran), que React
 * execute apres elle, avant de demonter l'ecran : datee d'avant, elle ne retablit pas la session dans l'onglet.
 */
let epoque = 0;

/** A lire au montage de l'ecran qui ecrit la session, puis a donner a chacune de ses ecritures. */
export function epoqueSessionAppels(): number {
  return epoque;
}

/** Fin de session du compte : la session d'appels est effacee, et aucun ecran monte avant ne la reecrit. */
export function revoquerSessionAppels(): void {
  epoque += 1;
  effacerSessionAppels();
}

/** Ecriture d'un ecran monte a l'epoque `depuis` ; rien si le compte a quitte l'onglet depuis. */
export function ecrireSessionAppels(etat: EtatSessionAppels, depuis: number): void {
  if (depuis !== epoque) return;
  try {
    window.sessionStorage.setItem(CLE_SESSION_APPELS, JSON.stringify(etat));
  } catch {
    /* stockage plein ou bloque : la session continue, sans reprise possible */
  }
}

export function effacerSessionAppels(): void {
  try {
    window.sessionStorage.removeItem(CLE_SESSION_APPELS);
  } catch {
    /* stockage bloque : il n'y a rien a effacer */
  }
}
